import { isAtLeast } from '../utils/roles';

/**
 * Identifies each navigation view category.
 */
export type ViewId = 'overview' | 'weekend' | 'planning' | 'communications' | 'admin' | 'settings';

/**
 * A logical grouping of navigation items displayed as a collapsible section.
 */
export interface ViewCategory {
  id: ViewId;
  label: string;
  icon: string;
  minRole?: string;
  defaultCollapsed?: boolean;
}

/**
 * A single navigation link belonging to a ViewCategory.
 */
export interface NavItem {
  path: string;
  label: string;
  icon: string;
  viewId: ViewId;
  minRole?: string;
  badge?: string | number;
  badgeSource?: string;
  subGroup?: string;
}

/**
 * A view category paired with its role-filtered navigation items.
 */
export interface FilteredViewCategory {
  category: ViewCategory;
  items: NavItem[];
}

/**
 * All view categories in display order.
 */
export const VIEW_CATEGORIES: ViewCategory[] = [
  { id: 'overview', label: 'Overview', icon: '📊' },
  { id: 'weekend', label: 'Weekend', icon: '🎉' },
  { id: 'planning', label: 'Planning', icon: '📋', minRole: 'Organizer' },
  { id: 'communications', label: 'Communications', icon: '📢' },
  { id: 'admin', label: 'Admin', icon: '🔐', minRole: 'Organizer' },
  { id: 'settings', label: 'Settings', icon: '⚙️' },
];

/**
 * All navigation items mapped to their view categories.
 */
export const NAV_ITEMS: NavItem[] = [
  // OVERVIEW
  { path: '/dashboard', label: 'Dashboard', icon: '📊', viewId: 'overview', minRole: 'Organizer' },
  { path: '/organizer', label: 'Organizer Panel', icon: '⚡', viewId: 'overview', minRole: 'Organizer' },
  { path: '/organizer/mobile', label: 'Mobile Toolkit', icon: '📱', viewId: 'overview', minRole: 'Delegate' },
  { path: '/volunteer', label: 'Volunteer Console', icon: '🙌', viewId: 'overview', minRole: 'Delegate' },
  { path: '/discussions', label: 'Discussions', icon: '💬', viewId: 'overview', minRole: 'Delegate' },
  { path: '/reports', label: 'Reports', icon: '📈', viewId: 'overview', minRole: 'Organizer' },

  // WEEKEND
  { path: '/my-weekend', label: 'My Weekend', icon: '🎉', viewId: 'weekend', minRole: 'Attendee' },
  { path: '/my-tasks', label: 'My Tasks', icon: '✅', viewId: 'weekend', minRole: 'Delegate' },
  { path: '/guest-toolkit', label: 'Guest Toolkit', icon: '🧳', viewId: 'weekend', minRole: 'Attendee' },
  { path: '/events', label: 'Events & Schedule', icon: '📅', viewId: 'weekend', minRole: 'Attendee' },
  { path: '/legacy-ball', label: 'Legacy Ball', icon: '🎭', viewId: 'weekend', minRole: 'Attendee' },
  { path: '/weekend-booklet', label: 'Weekend Booklet', icon: '📖', viewId: 'weekend', minRole: 'Delegate' },
  { path: '/kongosa-box', label: 'Kongosa Box', icon: '📡', viewId: 'weekend', minRole: 'Delegate' },
  { path: '/logistics-center', label: 'K-LOC', icon: '🏢', viewId: 'weekend', minRole: 'Delegate' },
  { path: '/packet-pickup', label: 'Packet Pickup', icon: '📦', viewId: 'weekend', minRole: 'Delegate' },
  { path: '/delivery-board', label: 'Delivery Board', icon: '📦', viewId: 'weekend', minRole: 'Delegate' },
  { path: '/marketplace', label: 'Marketplace', icon: '🛍️', viewId: 'weekend', minRole: 'Attendee' },
  { path: '/nearby', label: 'Nearby Places', icon: '📍', viewId: 'weekend', minRole: 'Attendee' },
  { path: '/weekend-tasks', label: 'Weekend Tasks', icon: '📋', viewId: 'weekend', minRole: 'Delegate' },
  { path: '/gx-console', label: 'GX Team Console', icon: '🎯', viewId: 'weekend', minRole: 'Delegate' },
  { path: '/guest-experience', label: 'Processes & Flows', icon: '🔄', viewId: 'weekend', minRole: 'Delegate' },
  { path: '/story-builder/interview', label: 'Story Builder', icon: '📖', viewId: 'weekend', minRole: 'Attendee' },
  { path: '/guest-journey', label: 'Guest Journey', icon: '🗺️', viewId: 'weekend', minRole: 'Delegate' },
  { path: '/qr-directory', label: 'QR Code Directory', icon: '📲', viewId: 'weekend', minRole: 'Delegate' },
  { path: '/checkin-training', label: 'Check-In Training', icon: '🎓', viewId: 'overview', minRole: 'Organizer' },
  { path: '/help-desk', label: 'Guest Help Desk', icon: '🪘', viewId: 'overview', minRole: 'Organizer' },
  { path: '/vendor-map', label: 'Vendor Map', icon: '🗺️', viewId: 'weekend', minRole: 'Attendee' },
  { path: '/vendor-showcase', label: 'Vendor Showcase', icon: '🏪', viewId: 'weekend', minRole: 'Attendee' },
  { path: '/food-catalog', label: 'Food Catalog', icon: '🍽️', viewId: 'weekend', minRole: 'Attendee' },
  { path: '/food-catalog/manage', label: 'Food Menu Admin', icon: '🍽️', viewId: 'admin', minRole: 'Organizer', subGroup: 'Content' },

  // PLANNING
  { path: '/tasks', label: 'Master Plan', icon: '📋', viewId: 'planning', minRole: 'Organizer' },
  { path: '/all-tasks', label: 'All Event Tasks', icon: '📝', viewId: 'weekend', minRole: 'Organizer' },
  { path: '/budget', label: 'Budget', icon: '💰', viewId: 'planning', minRole: 'Organizer' },
  { path: '/budget-submissions', label: 'Budget Submissions', icon: '📝', viewId: 'planning', minRole: 'Organizer' },
  { path: '/fundraising', label: 'Fundraising', icon: '🎯', viewId: 'planning', minRole: 'Organizer' },
  { path: '/guest-experience', label: 'Processes & Flows', icon: '🎯', viewId: 'planning', minRole: 'Organizer' },
  { path: '/gx-dashboard/e1000000-0000-0000-0000-000000000003', label: 'GX Dashboard', icon: '🎙️', viewId: 'planning', minRole: 'Organizer' },
  { path: '/registrations', label: 'Registrations', icon: '🎫', viewId: 'planning', minRole: 'Organizer' },
  { path: '/seating-engine', label: 'Seating Engine', icon: '🧠', viewId: 'planning', minRole: 'Organizer' },
  { path: '/venues', label: 'Venues', icon: '📍', viewId: 'planning', minRole: 'Organizer' },
  { path: '/vendors', label: 'Vendors', icon: '🏪', viewId: 'planning', minRole: 'Organizer' },

  // COMMUNICATIONS
  { path: '/announcements', label: 'Announcements', icon: '📢', viewId: 'communications' },
  { path: '/login-broadcasts', label: 'Login Broadcasts', icon: '📣', viewId: 'communications', minRole: 'Organizer' },
  { path: '/send-text', label: 'Send Text', icon: '📱', viewId: 'communications', minRole: 'Organizer' },
  { path: '/committees', label: 'Sub-Committees', icon: '👥', viewId: 'communications', minRole: 'Delegate' },
  { path: '/chapters', label: 'Chapters', icon: '🌍', viewId: 'communications' },
  { path: '/meetings', label: 'Meeting Minutes', icon: '📝', viewId: 'communications', minRole: 'Organizer' },
  { path: '/knowledge', label: 'Knowledge Base', icon: '🧠', viewId: 'communications', minRole: 'Delegate' },
  { path: '/guest-questions', label: 'Guest Questions', icon: '❓', viewId: 'communications', minRole: 'Attendee' },
  { path: '/faq-manage', label: 'FAQ Management', icon: '⚙️', viewId: 'communications', minRole: 'Organizer' },
  { path: '/krio-fun-facts', label: 'Krio Fun Facts', icon: '🎲', viewId: 'communications' },

  // ADMIN — People
  { path: '/members', label: 'KDU GA Members', icon: '👤', viewId: 'admin', minRole: 'Organizer', subGroup: 'People' },
  { path: '/sponsors', label: 'Sponsors', icon: '⭐', viewId: 'admin', minRole: 'Organizer', subGroup: 'People' },
  { path: '/users', label: 'User Management', icon: '🔐', viewId: 'admin', minRole: 'Organizer', subGroup: 'People' },

  // ADMIN — Content
  { path: '/krio-fun-facts/manage', label: 'Fun Facts Manage', icon: '🎲', viewId: 'admin', minRole: 'Organizer', subGroup: 'Content' },
  { path: '/story-builder/admin', label: 'Story Builder Admin', icon: '📖', viewId: 'admin', minRole: 'Organizer', subGroup: 'Content' },

  // ADMIN — System
  { path: '/admin-settings', label: 'Admin Settings', icon: '🛠️', viewId: 'admin', minRole: 'Admin', subGroup: 'System' },
  { path: '/admin/error-reports', label: 'Bug Reports', icon: '🐛', viewId: 'admin', minRole: 'Admin', subGroup: 'System' },
  { path: '/admin/screen-aliases', label: 'Screen Aliases', icon: '🏷️', viewId: 'admin', minRole: 'Admin', subGroup: 'System' },
  { path: '/admin/login-audit', label: 'Login Audit', icon: '🔑', viewId: 'admin', minRole: 'Admin', subGroup: 'System' },
  { path: '/admin/document-library', label: 'Document Library', icon: '📚', viewId: 'admin', minRole: 'Admin', subGroup: 'System' },
  { path: '/observability', label: 'Observability', icon: '📊', viewId: 'admin', minRole: 'Admin', subGroup: 'System' },
  { path: '/admin/email-upgrades', label: 'Email Upgrades', icon: '✉️', viewId: 'admin', minRole: 'Admin', subGroup: 'System' },

  // SETTINGS
  { path: '/settings', label: 'User Preferences', icon: '⚙️', viewId: 'settings' },
];

/**
 * Returns the navigation categories and items visible to a user with the given role.
 * Categories with no visible items after filtering are excluded.
 */
export function getNavForRole(role: string): FilteredViewCategory[] {
  const result: FilteredViewCategory[] = [];

  for (const category of VIEW_CATEGORIES) {
    // Skip entire category if user doesn't meet the category's minRole
    if (category.minRole && !isAtLeast(role, category.minRole)) {
      continue;
    }

    // Filter items belonging to this category by role
    const items = NAV_ITEMS.filter(
      (item) => item.viewId === category.id && (!item.minRole || isAtLeast(role, item.minRole))
    );

    // Only include categories that have at least one visible item
    if (items.length > 0) {
      result.push({ category, items });
    }
  }

  return result;
}
